import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row
} from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect } from 'react'

import {
  useStocksServiceCreateFundingSourceEndpoint,
  useStocksServiceGetFundingSourceEndpoint,
  useStocksServiceGetListFundingSourceEndpointKey,
  useStocksServiceUpdateFundingSourceEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import { EditableFormItem, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const FundingSourceModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const { data, isLoading } = useStocksServiceGetFundingSourceEndpoint(
    {
      id: String(id)
    },
    [id],
    { enabled: !!id }
  )

  const { mutate: create } = useStocksServiceCreateFundingSourceEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetListFundingSourceEndpointKey]
      })
      message.success(t('funding_source.create_success'))
      onCancel()
    }
  })

  const { mutate, isPending } = useStocksServiceUpdateFundingSourceEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetListFundingSourceEndpointKey]
      })
      message.success(t('common.save_success'))
      onCancel()
    }
  })

  const onSave = async () => {
    const values = await form.validateFields()

    const payload = {
      name: {
        ro: values?.name?.ro,
        ru: values?.name?.ru
      },
      comments: values?.comments,
      order: values?.order
    }

    id
      ? mutate({ id: String(id), requestBody: payload })
      : create({
          requestBody: payload
        })
  }

  useEffect(() => {
    if (data)
      form.setFieldsValue({
        name: {
          ro: data?.name?.ro,
          ru: data?.name?.ru
        },
        comments: data?.comments,
        order: data?.order
      })
  }, [data])

  return (
    <Modal
      open
      centered
      loading={isLoading}
      width={700}
      maskClosable={false}
      footer={null}
      title={t(id ? 'common.edit' : 'funding_source.create')}
      onCancel={onCancel}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Row gutter={[24, 24]}>
          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['name', langKey]}
                  label={`${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          <Col span={12}>
            <Form.Item name="order" label={t('common.order')}>
              <InputNumber
                className="full-width"
                placeholder={t('common.order')}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="comments" label={t('common.comments')}>
              <Input.TextArea rows={5} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t('common.save')}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isPending }}
              >
                <Button type="primary">{t('common.save')}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
